import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { combineLatest, map, Subject, takeUntil } from 'rxjs';
import { Grade } from '../../../../../models/grade.school.model';
import { Group } from '../../../../../models/group.model';
import { AppState } from '../../../../../state/appstate';
import * as gradeSelector from '../../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../../state/selectors/group.selector';

@Component({
  selector: 'app-students-by-grade',
  standalone: true,
  imports: [BaseChartDirective],
  templateUrl: './students-by-grade.component.html',
  styleUrl: './students-by-grade.component.css',
})
export class StudentsByGradeComponent implements OnInit, OnDestroy {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective<'bar'> | undefined;
  route: ActivatedRoute = inject(ActivatedRoute);

  colorScheme: any = [
    '#01051e',
    // '#020a34',
    // '#020f49',
    '#02165f',
    // '#031d75',
    // '#03258b',
    '#032ea1',
    // '#0239b7',
    // '#0244cd',
    '#0250e4',
    // '#015dfa',
    // '#1470fe',
    '#2982ff',
  ];

  store: Store<AppState> = inject(Store);

  groupId: string | null = null;

  grades: Grade[] | null | undefined;
  group: Group | null | undefined;

  exit$ = new Subject<void>();

  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
  };

  public barChartType: ChartType = 'bar';
  public barChartLegend = true;
  public barChartPlugins = [];

  public barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Students',
        backgroundColor: '#01051e',
      },
    ],
  };

  private params$ = combineLatest({
    params: this.route.paramMap,
  }).pipe(
    map(({ params }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.params$.subscribe(({ groupId }) => {
      this.groupId = groupId;
      this.store.select(gradeSelector.getGradesByGroup(groupId));
    });
  }

  setChartData() {
    if (this.group?.gradeStats && this.grades) {
      const labels: string[] = [];
      const data: number[] = [];

      this.grades.forEach((grade) => {
        labels.push(grade.gradeName);
        data.push(this.group?.gradeStats![grade.id!] ?? 0);
      });

      labels.push('Unassigned');
      data.push(this.group?.gradeStats!['unassigned'] ?? 0);

      // Update by reference
      this.barChartData.labels = labels;
      this.barChartData.datasets[0].data = data;

      this.chart?.update();
    }
  }

  ngOnInit(): void {
    this.store
      .select(gradeSelector.getGradesByGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => {
        this.grades = grades;
        this.setChartData();
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.setChartData();
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onSelect(event: any) {
    // console.log(event);
  }
}
